import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Search as SearchIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { MovieDetails } from "@/components/MovieDetails";
import { SearchResultRow } from "@/components/SearchResultRow";
import { SearchRateDialog } from "@/components/SearchRateDialog";

const TOASTS = { left: "Not interested · won't show again", right: "Added to Watchlist", up: "Seen · Liked it", down: "Seen · Didn't like it" };
const STATUS_BY_DIR = { left: "passed", right: "watchlist", up: "liked", down: "disliked" };

function useMovieSearch(query) {
  const [state, setState] = useState({ results: [], page: 0, totalPages: 0, total: 0, loading: false });

  const fetchPage = async (q, page) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/search", { params: { q, page } });
      setState((s) => ({
        results: page === 1 ? data.results : [...s.results, ...data.results.filter((m) => !s.results.some((r) => r.movie_id === m.movie_id))],
        page: data.page, totalPages: data.total_pages, total: data.total_results, loading: false,
      }));
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    const q = query.trim();
    if (!q) { setState({ results: [], page: 0, totalPages: 0, total: 0, loading: false }); return; }
    const t = setTimeout(() => fetchPage(q, 1), 350);
    return () => clearTimeout(t);
  }, [query]);

  const setStatus = (id, status) =>
    setState((s) => ({ ...s, results: s.results.map((m) => (m.movie_id === id ? { ...m, status } : m)) }));
  return { ...state, more: () => fetchPage(query.trim(), state.page + 1), setStatus };
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [active, setActive] = useState(null);
  const [details, setDetails] = useState(null);
  const { results, page, totalPages, total, loading, more, setStatus } = useMovieSearch(query);

  const onChange = (v) => { setQuery(v); setParams(v ? { q: v } : {}, { replace: true }); };

  const record = async (movie, direction) => {
    setActive(null);
    const prev = movie.status || null;
    setStatus(movie.movie_id, STATUS_BY_DIR[direction]);
    try {
      await api.post("/swipe", { movie_id: movie.movie_id, direction, source: "search" });
      toast(TOASTS[direction], {
        id: "search-feedback", description: movie.title, duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/swipe/undo", { movie_id: movie.movie_id });
              setStatus(movie.movie_id, prev);
              toast("Undone", { id: "search-feedback", description: movie.title, duration: 1400 });
            } catch (err) { toast.error(formatApiError(err)); }
          },
        },
      });
    } catch (e) {
      setStatus(movie.movie_id, prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader eyebrow="Seen it somewhere else?" title="Search movies"
        sub="Find any movie and swipe to log it: up liked, down didn't, right for your watchlist, left not interested." />
      <div className="relative mb-6">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input autoFocus value={query} onChange={(e) => onChange(e.target.value)} placeholder="Search by title…" data-testid="search-input"
          className="w-full h-14 pl-12 pr-12 rounded-2xl border bg-card text-base outline-none focus:ring-2 focus:ring-primary/50" />
        {query && (
          <button onClick={() => onChange("")} aria-label="Clear search" data-testid="search-clear-button"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-muted-foreground hover:bg-accent">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {query.trim() && !loading && page > 0 && results.length === 0 && (
        <p className="text-center text-muted-foreground py-10" data-testid="search-no-results">No movies found for "{query.trim()}".</p>
      )}
      {results.length > 0 && (
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3" data-testid="search-count">{total} results</p>
      )}
      <ul className="space-y-2.5" data-testid="search-results">
        {results.map((m, i) => <SearchResultRow key={m.movie_id} movie={m} index={i} onOpen={setActive} />)}
      </ul>
      {loading && <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-6" />}
      {!loading && page > 0 && page < totalPages && (
        <button onClick={more} data-testid="search-load-more"
          className="mt-6 mx-auto block h-11 px-6 rounded-full border font-semibold hover:bg-accent">Load more</button>
      )}
      <SearchRateDialog movie={active} onClose={() => setActive(null)} onDone={record} onDetails={setDetails} />
      <MovieDetails movie={details} onClose={() => setDetails(null)} />
    </div>
  );
}
